import "react-phone-input-2/lib/material.css";

import { FormControl, FormControlProps, FormHelperText, FormLabel, styled } from "@mui/material";
import { AsYouType, isPossiblePhoneNumber } from "libphonenumber-js";
import { isEmpty } from "lodash-es";
import React, { useCallback, useMemo } from "react";
import { Controller, useFormContext, ValidateResult } from "react-hook-form";
import PhoneInput from "react-phone-input-2";

import { SENSITIVE_CLASS_NAME } from "../../utils/highlightUtils";
import { getMaybeErrorMessage } from "./getMaybeErrorMessage";

const EXCLUDED_COUNTRY_CODES: string[] = ["cn"];

const StyledPhoneInput = styled(PhoneInput)(({ theme, disabled }) => ({
    "&.react-tel-input .form-control": {
        height: 40,
        width: "100%",
        padding: "8.5px 14px 8.5px 58px",
        borderRadius: "4px",
        transition: "none",
        "&:disabled": {
            color: theme.palette.text.disabled,
        },
        "&:disabled:hover": {
            borderColor: "#cacaca",
        },
        "&:focus": {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
        },
    },
    "& .flag-dropdown": {
        // match opacity of the text when disabled
        opacity: disabled ? 0.38 : undefined,
    },
}));

interface IProps {
    label?: React.ReactNode;
    fullPhoneFieldName: string; // this field gets the full number: 18885557777
    countryCodeFieldName?: string; // this field gets the country code: 1
    localNumberFieldName?: string; // this field gets the phone number: 8885557777
    defaultValue?: string;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    sx?: FormControlProps["sx"];
}

const maybeAddLeadingPlus = (fullPhoneNumber: string) => {
    return fullPhoneNumber[0] === "+" ? fullPhoneNumber : `+${fullPhoneNumber}`;
};

export const isPhoneNumberPossible = (fullPhoneNumber: string) => {
    return isPossiblePhoneNumber(maybeAddLeadingPlus(fullPhoneNumber));
};

export const FormPhoneInput: React.FC<IProps> = ({
    label,
    fullPhoneFieldName,
    countryCodeFieldName,
    localNumberFieldName,
    defaultValue = "",
    required,
    disabled,
    className,
    sx,
}) => {
    const { control, formState, setValue } = useFormContext();
    const errorMessage = getMaybeErrorMessage(formState.errors, fullPhoneFieldName);

    const parser = useMemo(() => new AsYouType(), []);

    const validatePhoneNumber = useCallback(
        (phoneNumber: string): ValidateResult => {
            if (isPhoneNumberPossible(maybeAddLeadingPlus(phoneNumber))) {
                return true;
            } else if (required) {
                return "Invalid phone number";
            }
            // Use the as-you-type parser to correctly differentiate between just a country code and an invalid phone number.
            parser.reset();
            parser.input(maybeAddLeadingPlus(phoneNumber));
            // If there's just a country code, that's equivalent to omitting an optional phone number
            if (!parser.getNationalNumber()) {
                return true;
            }
            return "Invalid phone number";
        },
        [parser, required],
    );

    return (
        <FormControl
            required={required}
            disabled={disabled}
            error={errorMessage != null}
            fullWidth={true}
            className={className}
            sx={sx}
        >
            {!isEmpty(label) && <FormLabel>{label}</FormLabel>}
            <Controller
                name={fullPhoneFieldName}
                control={control}
                rules={{ required, validate: validatePhoneNumber }}
                defaultValue={defaultValue}
                render={({ field: { ref, value, onChange, ...field } }) => {
                    if (value && value[0] !== "+") {
                        value = `+${value}`;
                    }

                    return (
                        <StyledPhoneInput
                            {...field}
                            country="us"
                            excludeCountries={EXCLUDED_COUNTRY_CODES}
                            specialLabel=""
                            value={value}
                            disabled={disabled}
                            inputProps={{ ref }}
                            inputClass={SENSITIVE_CLASS_NAME}
                            onChange={(nextValue, data) => {
                                if (countryCodeFieldName || localNumberFieldName) {
                                    const phoneCountryCode = "dialCode" in data ? data.dialCode : "";
                                    const localPhoneNumber = nextValue.slice(phoneCountryCode.length);

                                    if (countryCodeFieldName) {
                                        setValue(countryCodeFieldName, phoneCountryCode);
                                    }

                                    if (localNumberFieldName) {
                                        setValue(localNumberFieldName, localPhoneNumber);
                                    }
                                }

                                onChange(nextValue);
                            }}
                        />
                    );
                }}
            />
            <FormHelperText>{errorMessage}</FormHelperText>
        </FormControl>
    );
};
